import PrototypePreviewNotice from './PrototypePreviewNotice';
import {
  AlertTriangle,
  Archive,
  Ban,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock3,
  Database,
  FileText,
  GitBranch,
  Info,
  Lock,
  MessageSquare,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  User,
  Workflow,
  XCircle,
} from 'lucide-react';

const episodes = [
  {
    ep: 11,
    title: '珠帘茶盏',
    state: 'blocked',
    draft: '首稿完成',
    revision: '二次修订后仍失败',
    warning: 2,
    critical: 1,
    archived: false,
    recheck: '未通过',
    attempts: 3,
    knowledge: '顾南枝不知道太后在珠帘后旁听；裴照夜知道茶盏碎裂但未公开。',
    prop: '黑色端砚已封存入档；茶盏碎片留在太后宫中。',
    revealLock: '端砚暗格 EP12 才可揭晓',
  },
  {
    ep: 12,
    title: '暗格第三层',
    state: 'safe',
    draft: '首稿通过',
    revision: '无修订',
    warning: 1,
    critical: 0,
    archived: true,
    recheck: '通过',
    attempts: 1,
    knowledge: '顾南枝通过师傅旧账知道暗格位置；谢临川只知道端砚可疑。',
    prop: '端砚暗格已打开，账册绢帛转移到谢临川手中。',
    revealLock: '暗格机制本集可揭晓',
  },
  {
    ep: 13,
    title: '御前翻供',
    state: 'review',
    draft: '首稿完成',
    revision: '外科修订中',
    warning: 3,
    critical: 0,
    archived: false,
    recheck: '待复查',
    attempts: 2,
    knowledge: '朝臣只知道账册存在，不知道账册来源；反派误以为证据已烧毁。',
    prop: '账册副本在谢临川袖中，原件仍锁在刑部暗柜。',
    revealLock: '幕后主使身份 EP16 前禁止说破',
  },
];

const issueGroups = [
  {
    type: 'critical',
    title: '严重问题',
    desc: '角色无来源知道信息，且触碰未揭晓伏笔锁。',
    items: ['顾文渊不能提前知道端砚暗格在第三层', '修订时不能顺手剖开端砚，EP12 才允许揭晓'],
  },
  {
    type: 'warning',
    title: '普通警告',
    desc: '不阻止候选展示，但需要用户确认或下一轮修订。',
    items: ['台词提到“半块碎砚”，与 prop_state 的“三瓣封存”不一致', '结尾钩子略弱，建议加强下一集点击理由'],
  },
];

const formalPanelItems = [
  {
    title: '一句话状态',
    desc: '用户先看到这集能不能继续：可入库 / 待确认 / 已拦截。',
    examples: ['EP11 已拦截', '不能进入下一集上下文'],
  },
  {
    title: '当前施工进度',
    desc: '用人话表达首稿和修订，不直接暴露底层字段。',
    examples: ['首稿完成', '已自动修订 2 次，仍有严重问题'],
  },
  {
    title: '阻塞原因摘要',
    desc: '只展示最关键的 warning / critical 结论，避免技术报告感。',
    examples: ['角色提前知道未公开信息', '道具状态与前集冲突'],
  },
  {
    title: '施工卡摘要',
    desc: '把 knowledge_state、prop_state、揭晓锁翻译成写作提示。',
    examples: ['谁知道 / 谁不知道', '道具当前在哪里', '本集不能说破什么'],
  },
  {
    title: '用户可执行动作',
    desc: '右侧面板只放会推动创作的动作。',
    examples: ['重写这一集', '查看候选对比', '人工确认采用'],
  },
];

const debugViewItems = [
  {
    title: 'attempts 原始轮次',
    desc: '保留给验收和排障，正式页只表达为“已修订几次”。',
    examples: ['attempts=3', 'MAX_FIX=2'],
  },
  {
    title: '独立复查明细',
    desc: '正式页只显示“复查未通过/待复查”；完整复查报告进调试视图。',
    examples: ['final recheck issues', 'passed=false'],
  },
  {
    title: 'critical / warning 原始列表',
    desc: '正式页展示摘要，验收视图保留逐条 fact_violated / suggestion。',
    examples: ['fact_violated', 'rewrite_instruction'],
  },
  {
    title: '入库链路细节',
    desc: '正式页只说“未入库”；调试视图看 content、facts、context 三条链路。',
    examples: ['不写 content', '不抽 facts', '不进 contextBuilder'],
  },
  {
    title: '完整状态账本',
    desc: 'knowledge_state、prop_state、揭晓锁完整 JSON 或长文本只放验收/调试。',
    examples: ['knowledge_state raw', 'prop_state raw', 'futureReveals raw'],
  },
];

function StatusPill({ tone = 'neutral', children }) {
  const toneMap = {
    safe: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    danger: 'border-rose-200 bg-rose-50 text-rose-700',
    warn: 'border-amber-200 bg-amber-50 text-amber-700',
    info: 'border-[#c9bedc] bg-[#eee9de] text-[#6f6196]',
    neutral: 'border-[#ded6c8] bg-white text-stone-500',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${toneMap[tone]}`}>
      {children}
    </span>
  );
}

function PlacementCard({ title, desc, examples, tone = 'neutral' }) {
  const toneMap = {
    official: 'border-[#c9bedc] bg-[#fbfaf6]',
    debug: 'border-stone-200 bg-white/70',
    neutral: 'border-[#ded6c8] bg-white/75',
  };
  return (
    <div className={`rounded-2xl border p-4 ${toneMap[tone]}`}>
      <p className="text-[13px] font-semibold text-stone-900">{title}</p>
      <p className="mt-1 text-[12px] leading-5 text-stone-500">{desc}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {examples.map(item => (
          <span key={item} className="rounded-full bg-[#eee9de] px-2.5 py-1 text-[11px] text-stone-600">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, tone = 'neutral' }) {
  const toneMap = {
    safe: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    danger: 'text-rose-700 bg-rose-50 border-rose-200',
    warn: 'text-amber-700 bg-amber-50 border-amber-200',
    info: 'text-[#6f6196] bg-[#eee9de] border-[#c9bedc]',
    neutral: 'text-stone-700 bg-white border-[#ded6c8]',
  };
  return (
    <div className={`rounded-2xl border p-4 ${toneMap[tone]}`}>
      <div className="flex items-center gap-2 text-[12px] opacity-75">
        <Icon size={14} />
        {label}
      </div>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function TimelineStep({ icon: Icon, title, desc, state }) {
  const isBad = state === 'bad';
  const isDone = state === 'done';
  return (
    <div className="flex gap-3">
      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${
        isBad
          ? 'border-rose-200 bg-rose-50 text-rose-600'
          : isDone
            ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
            : 'border-[#ded6c8] bg-white text-stone-400'
      }`}>
        <Icon size={15} />
      </div>
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-stone-800">{title}</p>
        <p className="mt-1 text-[12px] leading-5 text-stone-500">{desc}</p>
      </div>
    </div>
  );
}

function EpisodeRow({ item, active }) {
  const stateTone = item.state === 'safe' ? 'safe' : item.state === 'blocked' ? 'danger' : 'warn';
  const stateText = item.state === 'safe' ? '可入库' : item.state === 'blocked' ? '已拦截' : '待复查';
  return (
    <button disabled aria-describedby="prototype-static-controls" className={`w-full rounded-2xl border p-4 text-left transition ${
      active
        ? 'border-[#c9bedc] bg-[#eee9de] shadow-sm'
        : 'border-[#ded6c8] bg-white/75 hover:bg-white'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          item.state === 'blocked' ? 'bg-rose-100 text-rose-600' : item.state === 'safe' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
        }`}>
          {item.state === 'blocked' ? <Ban size={16} /> : item.state === 'safe' ? <CheckCircle2 size={16} /> : <Clock3 size={16} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-[14px] font-semibold text-stone-900">EP{item.ep} · {item.title}</p>
            <StatusPill tone={stateTone}>{stateText}</StatusPill>
          </div>
          <p className="mt-2 text-[12px] text-stone-500">{item.draft} / {item.revision}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <StatusPill tone={item.critical > 0 ? 'danger' : 'safe'}>critical {item.critical}</StatusPill>
            <StatusPill tone={item.warning > 0 ? 'warn' : 'safe'}>warning {item.warning}</StatusPill>
            <StatusPill tone="info">attempts {item.attempts}</StatusPill>
          </div>
        </div>
        <ChevronRight size={15} className="mt-1 text-stone-400" />
      </div>
    </button>
  );
}

function StateBlock({ icon: Icon, title, children, tone = 'neutral' }) {
  const toneMap = {
    danger: 'border-rose-200 bg-rose-50/70',
    warn: 'border-amber-200 bg-amber-50/70',
    safe: 'border-emerald-200 bg-emerald-50/70',
    neutral: 'border-[#ded6c8] bg-white/75',
  };
  return (
    <section className={`rounded-2xl border p-4 ${toneMap[tone]}`}>
      <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-stone-800">
        <Icon size={15} className="text-[#6f6196]" />
        {title}
      </div>
      <div className="text-[12px] leading-6 text-stone-600">{children}</div>
    </section>
  );
}

export default function PrototypeAgentStatusPage() {
  const active = episodes[0];

  return (
    <div className="prototype-preview-shell"><PrototypePreviewNotice /><div className="prototype-preview-content"><div className="min-h-full bg-[#f4f1e9] text-stone-900">
      <header className="border-b border-[#ded6c8] bg-[#fbfaf6]/95 px-8 py-5 backdrop-blur">
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#eee9de] text-[#6f6196]">
            <Workflow size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-[22px] font-semibold">Agent 状态信息层级 / 施工卡 UI</h1>
              <StatusPill tone="info">纯前端层级稿</StatusPill>
            </div>
            <p className="mt-1 text-[13px] text-stone-500">
              不接真实接口；本页只决定哪些信息进入正式写剧本右侧面板，哪些留在调试/验收视图。
            </p>
          </div>
          <button disabled aria-describedby="prototype-static-controls" className="rounded-2xl border border-[#ded6c8] bg-white px-4 py-2.5 text-[13px] text-stone-600">
            <MessageSquare size={14} className="mr-1.5 inline" />
            回到写作助手
          </button>
        </div>
      </header>

      <section className="px-8 py-6">
        <div className="grid gap-6 xl:grid-cols-2">
          <div className="rounded-3xl border border-[#c9bedc] bg-[#eee9de]/70 p-5">
            <div className="mb-4 flex items-center gap-2">
              <MessageSquare size={17} className="text-[#6f6196]" />
              <div>
                <h2 className="text-[16px] font-semibold text-stone-900">以后进正式写剧本页右侧面板</h2>
                <p className="mt-1 text-[12px] text-stone-500">面向创作者，只保留能帮助继续写作和决策的信息。</p>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {formalPanelItems.map(item => <PlacementCard key={item.title} {...item} tone="official" />)}
            </div>
          </div>

          <div className="rounded-3xl border border-[#ded6c8] bg-[#fbfaf6] p-5">
            <div className="mb-4 flex items-center gap-2">
              <ShieldCheck size={17} className="text-stone-500" />
              <div>
                <h2 className="text-[16px] font-semibold text-stone-900">只留在调试 / 验收视图</h2>
                <p className="mt-1 text-[12px] text-stone-500">面向开发、验收和排障，保留原始字段和完整证据。</p>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {debugViewItems.map(item => <PlacementCard key={item.title} {...item} tone="debug" />)}
            </div>
          </div>
        </div>
      </section>

      <div className="px-8">
        <div className="rounded-2xl border border-[#ded6c8] bg-white/70 px-4 py-3 text-[12px] leading-5 text-stone-500">
          下方保留为“调试 / 验收视图样例”：用于确认字段完整性和异常链路，不代表这些信息都会进入正式写剧本右侧面板。
        </div>
      </div>

      <main className="grid gap-6 px-8 py-6 lg:grid-cols-[360px_1fr_390px]">
        <aside className="space-y-4">
          <div className="rounded-3xl border border-[#ded6c8] bg-[#fbfaf6] p-5">
            <p className="text-[13px] font-semibold text-stone-800">连续写作队列</p>
            <p className="mt-1 text-[12px] text-stone-400">按集查看施工状态，blocked 集不进入后续上下文。</p>
            <div className="mt-4 space-y-3">
              {episodes.map((item, index) => <EpisodeRow key={item.ep} item={item} active={index === 0} />)}
            </div>
          </div>

          <div className="rounded-3xl border border-[#ded6c8] bg-[#fbfaf6] p-5">
            <p className="text-[13px] font-semibold text-stone-800">队列规则</p>
            <div className="mt-4 space-y-3">
              <TimelineStep icon={FileText} title="首稿生成" desc="首稿只进入候选态，等待门禁判断。" state="done" />
              <TimelineStep icon={RefreshCw} title="自动修订" desc="critical 才触发修订，最多 2 次，attempts 记录总轮次。" state="done" />
              <TimelineStep icon={Ban} title="失败拦截" desc="重写后仍 critical，不入库、不抽事实、不进入下一集上下文。" state="bad" />
            </div>
          </div>
        </aside>

        <section className="space-y-5">
          <div className="rounded-3xl border border-[#ded6c8] bg-[#fbfaf6] p-6">
            <div className="mb-5 flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
                <ShieldAlert size={22} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-[20px] font-semibold">EP{active.ep} · {active.title}</h2>
                  <StatusPill tone="danger">blocked 候选稿</StatusPill>
                  <StatusPill tone="warn">需要人工处理</StatusPill>
                </div>
                <p className="mt-2 text-[13px] leading-6 text-stone-500">
                  门禁发现 critical 后已拦截。该稿可以给用户查看和比较，但不能作为正式正文、不能抽取事实、不能进入下一集上下文。
                </p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <MetricCard icon={Sparkles} label="首稿状态" value="完成" tone="info" />
              <MetricCard icon={RefreshCw} label="修订状态" value="失败" tone="danger" />
              <MetricCard icon={AlertTriangle} label="warning" value={active.warning} tone="warn" />
              <MetricCard icon={ShieldAlert} label="critical" value={active.critical} tone="danger" />
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-4">
              <MetricCard icon={Database} label="是否入库" value="否" tone="danger" />
              <MetricCard icon={ShieldCheck} label="独立复查" value="未通过" tone="danger" />
              <MetricCard icon={GitBranch} label="attempts" value={`${active.attempts}/3`} tone="info" />
              <MetricCard icon={Lock} label="揭晓锁" value="锁定" tone="warn" />
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            {issueGroups.map(group => (
              <section key={group.type} className={`rounded-3xl border p-5 ${
                group.type === 'critical'
                  ? 'border-rose-200 bg-rose-50/75'
                  : 'border-amber-200 bg-amber-50/75'
              }`}>
                <div className="mb-3 flex items-center gap-2">
                  {group.type === 'critical'
                    ? <XCircle size={17} className="text-rose-600" />
                    : <AlertTriangle size={17} className="text-amber-600" />
                  }
                  <h3 className="text-[15px] font-semibold text-stone-900">{group.title}</h3>
                </div>
                <p className="text-[12px] leading-5 text-stone-600">{group.desc}</p>
                <div className="mt-4 space-y-2">
                  {group.items.map(item => (
                    <div key={item} className="flex gap-2 rounded-2xl bg-white/70 px-3 py-2.5 text-[12px] leading-5 text-stone-700">
                      <Circle size={8} className="mt-1.5 shrink-0 fill-current text-stone-400" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="rounded-3xl border border-[#ded6c8] bg-[#fbfaf6] p-5">
            <div className="mb-4 flex items-center gap-2">
              <Archive size={16} className="text-[#6f6196]" />
              <h3 className="text-[15px] font-semibold">入库决策</h3>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <StateBlock icon={Ban} title="正式正文" tone="danger">不写入 agent_episodes.content；保留为 blocked candidate。</StateBlock>
              <StateBlock icon={Database} title="事实入库" tone="danger">不抽取 story_fact_candidates，避免错误事实污染 canon。</StateBlock>
              <StateBlock icon={Workflow} title="后续上下文" tone="danger">contextBuilder 必须跳过该候选稿，下一集不能继承它。</StateBlock>
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <StateBlock icon={Info} title="knowledge_state" tone="warn">
            <p>{active.knowledge}</p>
            <div className="mt-3 rounded-2xl bg-white/70 p-3">
              <p className="font-medium text-stone-800">UI 目的</p>
              <p className="mt-1">让用户看到“谁知道 / 谁不知道”，防止角色提前知情或重复抖已经公开的信息。</p>
            </div>
          </StateBlock>

          <StateBlock icon={Archive} title="prop_state" tone="warn">
            <p>{active.prop}</p>
            <div className="mt-3 rounded-2xl bg-white/70 p-3">
              <p className="font-medium text-stone-800">UI 目的</p>
              <p className="mt-1">把道具当前位置、形态、归属展示清楚，避免修订时把已封存道具写回现场。</p>
            </div>
          </StateBlock>

          <StateBlock icon={Lock} title="揭晓锁状态" tone="danger">
            <p>{active.revealLock}</p>
            <div className="mt-3 rounded-2xl border border-rose-200 bg-white/80 p-3">
              <p className="font-medium text-rose-700">本集禁区</p>
              <p className="mt-1">只允许铺垫异常、反常、起疑；禁止说破机制、谜底或来源。</p>
            </div>
          </StateBlock>

          <div className="rounded-3xl border border-[#ded6c8] bg-[#fbfaf6] p-5">
            <div className="mb-4 flex items-center gap-2">
              <User size={16} className="text-[#6f6196]" />
              <h3 className="text-[15px] font-semibold">人工动作</h3>
            </div>
            <div className="space-y-2">
              <button disabled aria-describedby="prototype-static-controls" className="w-full rounded-2xl bg-[#8c7db0] px-4 py-3 text-left text-[13px] font-medium text-white shadow-lg shadow-[#8c7db0]/20">
                重写这一集
                <span className="mt-1 block text-[11px] font-normal text-white/70">带 critical 指令重新生成，不污染正式稿。</span>
              </button>
              <button disabled aria-describedby="prototype-static-controls" className="w-full rounded-2xl border border-[#ded6c8] bg-white px-4 py-3 text-left text-[13px] text-stone-700">
                查看原稿 vs 候选稿
                <span className="mt-1 block text-[11px] text-stone-400">用于判断是否强制采用或继续修订。</span>
              </button>
              <button disabled aria-describedby="prototype-static-controls" className="w-full rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-left text-[13px] text-rose-700">
                强制入库
                <span className="mt-1 block text-[11px] text-rose-500">高风险动作，需要二次确认。</span>
              </button>
            </div>
          </div>
        </aside>
      </main>
    </div></div></div>
  );
}
